import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, ShoppingCart, Leaf, ShieldCheck, HeartPulse, Sprout } from "lucide-react";
import { Stars } from "@/components/StarRating";
import { imgSrc } from "@/lib/img";
import { useImg } from "@/lib/useImg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANDING_IMG = (slug) => `/images/categories-landing/${slug}/product.jpg`;
const MOCK_BESTSELLERS = [
  { name: "Moringa Leaf Powder", rating: 4.8, count: 260, price: 449, badge: "Best Seller", badgeColor: "#E39A2B", img: LANDING_IMG("moringa-greens") },
  { name: "Ashwagandha Root", rating: 4.7, count: 410, price: 499, badge: "Trending", badgeColor: "#D64B6A", img: LANDING_IMG("adaptogens-herbs") },
  { name: "Mixed Seeds & Nuts", rating: 4.6, count: 180, price: 399, badge: "New Launch", badgeColor: "#2E8B57", img: LANDING_IMG("seeds-nuts") },
  { name: "Daily Superfood Blend", rating: 4.7, count: 320, price: 599, badge: "Best Seller", badgeColor: "#E39A2B", img: LANDING_IMG("superfood-blends") },
  { name: "Plant Protein Powder", rating: 4.6, count: 290, price: 699, badge: "Premium", badgeColor: "#C0314B", img: LANDING_IMG("proteins-plant-powders") },
  { name: "Wellness Latte Mix", rating: 4.5, count: 150, price: 549, badge: "Trending", badgeColor: "#D64B6A", img: LANDING_IMG("wellness-essentials") },
  { name: "Kids Nutrition Mix", rating: 4.8, count: 210, price: 499, badge: "New Launch", badgeColor: "#2E8B57", img: LANDING_IMG("kids-nutrition") },
];

const FEATURES = [
  { icon: Leaf, title: "100% Natural", text: "Pure ingredients. No compromises." },
  { icon: ShieldCheck, title: "No Artificial Additives", text: "Clean, simple, wholesome." },
  { icon: HeartPulse, title: "Better Nutrition", text: "For a healthier, happier you." },
  { icon: Sprout, title: "Sustainable Choices", text: "Good for you. Good for the planet." },
];

const Mandala = ({ color }) => {
  const petals = (count, rInner, rTip, width, idPrefix) =>
    Array.from({ length: count }).map((_, i) => (
      <path
        key={`${idPrefix}-${i}`}
        d={`M100 100 Q ${100 + width} ${100 - rInner}, 100 ${100 - rTip} Q ${100 - width} ${100 - rInner}, 100 100 Z`}
        transform={`rotate(${(360 / count) * i} 100 100)`}
      />
    ));
  const dots = (count, r) =>
    Array.from({ length: count }).map((_, i) => {
      const a = ((360 / count) * i * Math.PI) / 180;
      return <circle key={`d-${i}`} cx={100 + r * Math.sin(a)} cy={100 - r * Math.cos(a)} r="2.6" />;
    });
  return (
    <svg viewBox="0 0 200 200" fill={color} className="w-full h-full">
      <g opacity="0.9">{petals(16, 52, 94, 11, "o")}</g>
      <g opacity="0.75">{petals(12, 30, 60, 13, "m")}</g>
      <g opacity="0.6">{dots(16, 72)}</g>
      <circle cx="100" cy="100" r="13" opacity="0.5" />
      <circle cx="100" cy="100" r="6" fill="#fff" opacity="0.6" />
    </svg>
  );
};

const CategoryCard = ({ cat, num, wide }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55 }}
    className={wide ? "sm:col-span-2 lg:col-span-4" : ""}
  >
    <Link
      to={`/category/${cat.slug}`}
      data-testid={`category-card-${cat.slug}`}
      style={{ backgroundColor: cat.color_bg }}
      className={`group relative block overflow-hidden rounded-3xl ${wide ? "min-h-[200px] sm:min-h-[240px]" : "min-h-[320px]"}`}
    >
      {cat.image && (
        <img
          src={imgSrc(cat.image)}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
      )}
      {/* left readability wash in the card colour */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(to right, ${cat.color_bg} 18%, ${cat.color_bg}cc 34%, transparent 62%)` }}
      />
      <div className={`relative z-10 p-7 ${wide ? "sm:p-10 max-w-md" : "max-w-[60%]"}`}>
        <span className="font-serif leading-none text-4xl sm:text-5xl" style={{ color: cat.color_accent }}>
          {num}
        </span>
        <h3 className="font-serif text-xl sm:text-2xl mt-3 leading-tight" style={{ color: cat.color_accent }}>
          {cat.name}
        </h3>
        {cat.description && <p className="text-sm mt-2 text-ink/70 leading-relaxed">{cat.description}</p>}
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-full border mt-6 transition-colors bg-white/40"
          style={{ borderColor: cat.color_accent, color: cat.color_accent }}
        >
          <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </Link>
  </motion.div>
);

const Bestsellers = () => {
  const scroller = useRef(null);
  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * 220, behavior: "smooth" });
  const addDemo = (name) => toast.success(`${name} added to cart (demo)`);
  return (
    <section className="container-nb py-16 sm:py-20" data-testid="bestsellers-section">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow !text-gold mb-2">Our Popular Picks</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-forest">Bestsellers</h2>
          <p className="text-mutedgreen text-sm mt-1">Loved by many. Trusted for good.</p>
        </div>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scrollBy(-1)} data-testid="bestsellers-prev" aria-label="Scroll left" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronLeft size={18} /></button>
          <button onClick={() => scrollBy(1)} data-testid="bestsellers-next" aria-label="Scroll right" className="w-10 h-10 rounded-full border border-forest/20 text-forest flex items-center justify-center hover:bg-forest hover:text-cream transition-colors"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div ref={scroller} className="flex gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {MOCK_BESTSELLERS.map((p, i) => (
          <div key={i} data-testid={`bestseller-${i}`} className="shrink-0 w-48 sm:w-52 bg-white rounded-2xl border border-forest/5 shadow-sm p-3 relative">
            <span className="absolute top-4 left-4 z-10 text-[9px] font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full" style={{ backgroundColor: p.badgeColor }}>{p.badge}</span>
            <div className="rounded-xl overflow-hidden bg-[#FBF7EC] aspect-square"><img src={p.img} alt={p.name} className="w-full h-full object-cover" loading="lazy" /></div>
            <h3 className="font-semibold text-forest text-sm mt-3 leading-tight">{p.name}</h3>
            <div className="flex items-center gap-1.5 mt-1"><Stars value={p.rating} size={11} /><span className="text-[10px] text-mutedgreen">{p.rating} ({p.count})</span></div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-forest font-bold">₹ {p.price}</p>
              <button onClick={() => addDemo(p.name)} data-testid={`bestseller-add-${i}`} aria-label="Add to cart" className="w-9 h-9 rounded-full bg-forest hover:bg-gold text-cream flex items-center justify-center transition-colors"><ShoppingCart size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function CategoryHub() {
  const [cats, setCats] = useState([]);
  const img = useImg();
  useEffect(() => {
    axios.get(`${API}/categories`).then(({ data }) => setCats(data || [])).catch(() => {});
  }, []);

  return (
    <div data-testid="category-hub-page" className="bg-cream">
      {/* Decorative botanical header */}
      <section className="relative overflow-hidden" data-testid="category-hub-header">
        <img src={img("categoryHubHero")} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-cream/35" />
        <div className="relative container-nb py-16 sm:py-24 text-center">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex items-center justify-center gap-4 mb-3">
            <span className="h-px w-10 bg-forest/40" />
            <span className="eyebrow !text-forest/70 tracking-[0.3em] !mb-0">Shop By</span>
            <span className="h-px w-10 bg-forest/40" />
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="font-serif text-4xl sm:text-5xl lg:text-6xl text-forest">
            Category
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.3 }} className="text-forest/70 max-w-xl mx-auto mt-5 text-base">
            Discover wholesome superfoods, thoughtfully chosen for every stage of your wellness journey.
          </motion.p>
        </div>
      </section>

      {/* Category grid */}
      <section className="container-nb pt-12 pb-8">
        {cats.length === 0 ? (
          <p className="text-center text-mutedgreen py-16">Categories are coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {cats.map((cat, i) => (
              <CategoryCard
                key={cat.id}
                cat={cat}
                num={String(i + 1).padStart(2, "0")}
                wide={i === cats.length - 1 && cats.length % 4 === 1}
              />
            ))}
          </div>
        )}
      </section>

      {/* Continuous soft botanical lower band: Bestsellers + brand values (separate from dark footer) */}
      <div className="relative bg-[#F4F8EE]">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-cream to-transparent" />
        <div className="relative">
          <Bestsellers />
          <section className="relative overflow-hidden" data-testid="features-section">
            <img src="/images/features-landscape.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover object-bottom" />
            <div className="absolute inset-0 bg-[#F4F8EE]/55" />
            <div className="relative container-nb pt-10 pb-20">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
                {FEATURES.map(({ icon: Icon, title, text }) => (
                  <div key={title} className="text-center">
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/70 text-forest mb-4 shadow-sm"><Icon size={24} /></span>
                    <h3 className="font-serif text-lg text-forest">{title}</h3>
                    <p className="text-mutedgreen text-sm mt-1 leading-relaxed">{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
